import { Injectable } from '@angular/core';
import { UtilDocumentId } from '@formbird/shared';
import { isString } from 'lodash';
import { StorageEventService } from '../event/storage-event.service';
import { OfflineStatusService } from '../offline-status/offline-status.service';
import { LoggedInUserService } from '../user/logged-in-user.service';
import { ConfigService } from '../config/config.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { IndexedDBQueueService } from '../indexeddb/indexed-dbqueue.service';
import { ClientConstants } from '../../constants/ClientConstants';

const logger = console;

let OfflinePollerService$;

@Injectable({
  providedIn: 'root'
})
export class OfflinePollerService {

  /**
   * Time to wait before another cycle of processing offline files is initiated.
   */
  static DEFAULT_UNSYNCED_RECORD_CHECK_INTERVAL = 5000;

  /**
   * The interval in which a poller instance would lookup local storage again and see if it could become the main poller
   * replacing the old poller. If for some reason the main poller is still active but was replaced, it must terminate itself.
   *
   * This would mean that if a poller becomes inactive (reaches the timeout limit) it will be replaced in less than
   * 30 seconds (DEFAULT_POLLER_LOOKUP_INTERVAL) time.
   */
  static DEFAULT_POLLER_LOOKUP_INTERVAL = 30 * 1000; //30 seconds

  /**
   * If the lastActive status is not updated and reaches a value greater than TIMEOUT, then it will be replaced.
   */
  static DEFAULT_POLLER_TIMEOUT = 30;//in seconds

  static LOCAL_STORAGE_MAIN_POLLER_KEY = 'MainOfflinePoller';

  private poller = {
    pollerId: UtilDocumentId.generateId(),
    sending: false, // active or inactive
    lastActive: null // rule: poller will expire if inactive for 30 seconds and another poller instance will replace it
  };

  private timer = null;

  constructor(
    private storageEventService: StorageEventService,
    private offlineStatusService: OfflineStatusService,
    private loggedInUserService: LoggedInUserService,
    private configService: ConfigService,
    private localStorageService: LocalStorageService,
    private indexedDBQueueService: IndexedDBQueueService
  ) {

    OfflinePollerService$ = this;

    const _self = this;

    // Add listener to reset the timer if the main poller's status is updated (for waiting pollers)
    this.storageEventService.addListener(function onPollerStatusUpdate(event) {

      if (event.key === OfflinePollerService.LOCAL_STORAGE_MAIN_POLLER_KEY) {

        let mainPoller = event.newValue;
        if (isString(mainPoller)) {
          try {
            mainPoller = JSON.parse(mainPoller);
          } catch (e) {
          }
        }

        if (mainPoller.pollerId === _self.poller.pollerId) { // if this is the main poller then ignore
          return;
        }

        // reset timer
        if (_self.timer != null) {
          clearTimeout(_self.timer);
          // Note that even the main poller will get informed, so inform the main poller to just ignore
          // it so it won't call the mainPollerSequence again.
          _self.start(true);
        }

      } else if (event.key === ClientConstants.LOCAL_STORAGE_OFFLINE_STATUS_KEY) {

        let newOfflineStatus = event.newValue;

        if (isString(newOfflineStatus)) {

          try {
            newOfflineStatus = JSON.parse(newOfflineStatus);
            if (newOfflineStatus) {
              _self.offlineStatusService.mergeOfflineStatusData(newOfflineStatus);
            }
          } catch (e) {
            logger.error(e);
          }
        }
      }
    });
  }

  private startup = true;

  start(isStorageEvent?) {

    const _self = this;

    //allows enabling or disabling of offline feature; assuming cachingEnabled is a boolean value
    if (!_self.loggedInUserService.getUserConfigItem('cachingEnabled') === true) {
      return;
    }

    let mainPoller = _self.getMainPoller();

    if (mainPoller && mainPoller.pollerId === _self.poller.pollerId && isStorageEvent === undefined) {
      _self.mainPollerSequence();
      return;

    } else if (mainPoller == null || !OfflinePollerService.isPollerActive(mainPoller)) {
      logger.info('Poller started and assigned as main. PollerID: ' + _self.poller.pollerId);

      _self.poller.lastActive = Date.now(); // timestamp in millis
      _self.updateMainPoller(_self.poller);

      _self.mainPollerSequence(false);

    } else {
      // still waiting for the main poller to die
      if (_self.startup) {
        logger.info('Offline poller not starting because there is already a poller. ' +
          'Poller will start if the existing poller is closed. PollerID: ' + _self.poller.pollerId);
      }

      let configuredPollerLookupInterval = _self.configService.clientConfig().pollerLookupInterval;
      if (!configuredPollerLookupInterval) {
        configuredPollerLookupInterval = OfflinePollerService.DEFAULT_POLLER_LOOKUP_INTERVAL;
      }

      _self.timer = setTimeout(function() {
        _self.start();
      }, configuredPollerLookupInterval);
    }

    _self.startup = false;

  }

  end() {
    if (this.timer != null) {
      clearTimeout(this.timer);
    }
  }

  private mainPollerSequence(needTimeout?) {

    const _self = this;

    _self.keepPollerAlive();

    _self.poller.sending = false;

    if (needTimeout === false) {
      _self.checkForUnsyncedRecords();

    } else {
      // Read from config to get latest timeout value
      const configuredSyncTime = _self.configService.clientConfig().unsyncedRecordCheckTime;
      const unsyncedRecordCheckTime = configuredSyncTime ? configuredSyncTime : OfflinePollerService.DEFAULT_UNSYNCED_RECORD_CHECK_INTERVAL;
      _self.timer = setTimeout(function() {
        _self.checkForUnsyncedRecords();
      }, unsyncedRecordCheckTime);
    }
  }

  private static isPollerActive(p) {
    if (p.lastActive == null) {
      return false;
    }

    const secondsSinceLastUpdate = Math.floor((Date.now() - p.lastActive) / 1000);
    return secondsSinceLastUpdate < OfflinePollerService.DEFAULT_POLLER_TIMEOUT;
  }

  /**
   * Keep alive poller if it's the main poller.
   * Called every time an item progresses (doc or file is sent/failed; file upload progresses)
   */
  private keepPollerAlive() {
    const mainPoller = OfflinePollerService$.getMainPoller();

    if (mainPoller.pollerId === OfflinePollerService$.poller.pollerId) {
      const secondsSinceLastUpdate = Math.floor((Date.now() - mainPoller.lastActive) / 1000);
      if (secondsSinceLastUpdate > 0) { // only update in a significant interval
        mainPoller.lastActive = Date.now();
        OfflinePollerService$.updateMainPoller(mainPoller);
      }
    }
  }

  private updateMainPoller(newValue) {
    OfflinePollerService$.localStorageService.setItem(OfflinePollerService.LOCAL_STORAGE_MAIN_POLLER_KEY, JSON.stringify(newValue));
  }

  private getMainPoller() {
    const mainPoller = OfflinePollerService$.localStorageService.getItem(OfflinePollerService.LOCAL_STORAGE_MAIN_POLLER_KEY);

    return mainPoller ? JSON.parse(mainPoller) : null;
  }

  private checkForUnsyncedRecords() {

    const _self = this;

    if (!_self.poller.sending) {

      _self.poller.sending = true;
      // send any offline objects
      _self.indexedDBQueueService.sendUnsyncedObjects(_self.keepPollerAlive).then(
        function successFunc() {
          _self.start();
        },
        function errorFunc(err) {
          logger.error('Synching offline objects failed: ' + err);
          _self.start();
        });
    }
  }

}
